// src/api/epk.ts
//
// Authenticated API functions for EPK management.
// Used by the main BandJerry app (builder, management, analytics).
// Public (unauthenticated) functions are in epkPublic.ts.

import api from './index';
import type {
  EpkPage,
  EpkFormState,
  EpkAnalytics,
  EpkUploadUrlResponse,
  TrackableUrl,
} from '@/types/epk';

// --- Authenticated endpoints ---

export async function getEpkPage(): Promise<{ page: EpkPage }> {
  const response = await api.get('/epk');
  return response.data;
}

export async function createEpkPage(
  data: EpkFormState,
): Promise<{ page: EpkPage }> {
  const response = await api.post('/epk', data);
  return response.data;
}

export async function updateEpkPage(
  data: Partial<EpkFormState>,
): Promise<{ page: EpkPage }> {
  const response = await api.patch('/epk', data);
  return response.data;
}

export async function deleteEpkPage(): Promise<void> {
  await api.delete('/epk');
}

export async function getEpkUploadUrl(
  filename: string,
  contentType: string,
): Promise<EpkUploadUrlResponse> {
  const response = await api.post('/epk/upload-url', {
    filename,
    content_type: contentType,
  });
  return response.data;
}

export async function getEpkAnalytics(
  range: '7d' | '30d' = '30d',
): Promise<EpkAnalytics> {
  const response = await api.get('/epk/analytics', { params: { range } });
  return response.data;
}

export async function exportEpkSignups(): Promise<Blob> {
  const response = await api.get('/epk/signups/export', {
    responseType: 'blob',
  });
  return response.data;
}

export async function getEpkSignupsEmailList(): Promise<string> {
  const response = await api.get('/epk/signups/email-list', {
    responseType: 'text',
  });
  return response.data;
}

// --- Trackable URL endpoints ---

export async function createEpkTrackableUrl(
  name: string,
): Promise<{ trackable_url: TrackableUrl }> {
  const response = await api.post('/epk/trackable-urls', { name });
  return response.data;
}

export async function deleteEpkTrackableUrl(
  trackingId: string,
): Promise<void> {
  await api.delete(`/epk/trackable-urls/${trackingId}`);
}
